const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
        <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-12 shadow-sm">
          <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Privacy Policy
          </h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-slate-600 leading-relaxed mb-6">
              <strong>Last Updated:</strong> February 7, 2026
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Commitment to Your Privacy
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              At Lucent Sky ESG, we respect your data and your privacy. We believe in transparency and want you to understand
              exactly how we collect, use, and protect your information when you use our services.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Information We Collect
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We collect only what is needed for delivery and grant reporting services:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li><strong>Contact Information:</strong> Name, email address, and business details you provide when creating an account or using our tools</li>
              <li><strong>Payment Information:</strong> Processed securely via Stripe for subscription billing</li>
              <li><strong>Usage Data:</strong> Basic analytics to improve our services (anonymized where possible)</li>
              <li><strong>Carbon Data:</strong> The emissions data and evidence documents you upload to the platform</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Financial Transactions
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              All financial transactions are processed via <strong>Stripe</strong>, a certified PCI Service Provider Level 1
              (the highest level of certification). <strong>We never store your card details.</strong> Your payment information
              goes directly to Stripe's secure servers, which are encrypted with industry-standard 256-bit SSL technology.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>To provide and maintain the Emissions Tracker Dashboard</li>
              <li>To generate personalized ESG reports based on your business profile</li>
              <li>To respond to your support inquiries and questions</li>
              <li>To send important status updates about your account or subscription</li>
              <li>To improve our products and services</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Data Security
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We implement industry-standard security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>256-bit SSL encryption for all data transmission</li>
              <li>Secure cloud infrastructure with regular backups</li>
              <li>Limited access to personal data (staff access on a need-to-know basis)</li>
              <li>Regular security audits and updates</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Third-Party Services
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use trusted third-party services to operate our business:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li><strong>Stripe:</strong> Payment processing (subject to Stripe's Privacy Policy)</li>
              <li><strong>Resend:</strong> Email delivery for account communications and report notifications</li>
              <li><strong>Supabase:</strong> Secure database and file storage hosting</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-6">
              These providers have their own privacy policies and are compliant with applicable data protection regulations.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Rights
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Under Australian Privacy Principles (APPs), you have the right to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Access your personal information we hold</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your data (subject to legal obligations)</li>
              <li>Opt-out of marketing communications (product updates excluded)</li>
              <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Data Retention
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We retain your information for as long as necessary to provide our services and comply with legal obligations.
              Purchase records are kept for 7 years as required by Australian tax law. You may request earlier deletion of
              non-essential data by contacting us.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Us About Privacy
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-[#EDF6F9] border-l-4 border-[#74A57F] rounded p-6 mb-6">
              <p className="text-slate-700 mb-2"><strong>Email:</strong> hello@lucentsky.com.au</p>
              <p className="text-slate-700 mb-2"><strong>Location:</strong> Doonan, Sunshine Coast, Queensland</p>
              <p className="text-slate-700"><strong>ABN:</strong> 12 345 678 901</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-6 mt-8">
              <p className="text-sm text-slate-600">
                <strong>Note:</strong> This Privacy Policy may be updated from time to time. We will notify you of any
                material changes via email or through a prominent notice on our website. Your continued use of our services
                after such changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;